import { CORE, getToken } from "../../lib/api";

export type AnalyticsExportFormat = "CSV" | "XLSX" | "PDF";

export interface AnalyticsExportRequest {
  format: AnalyticsExportFormat;
  start: Date;
  end: Date;
  agentId?: string | null;
  workflowId?: string | null;
  targetName?: string | null;
  filters?: {
    search?: string;
    search_field?: string;
    status?: string;
    agent_name?: string;
    version_name?: string;
    voice?: string;
    campaign?: string;
    max_duration?: string;
    duration_operator?: string;
  };
}

function formatDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function safeFilename(value: string) {
  return value.trim().replace(/[^a-zA-Z0-9_-]+/g, "_").replace(/^_+|_+$/g, "");
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

async function fetchExportBlob(path: string, params: URLSearchParams) {
  const response = await fetch(`${CORE}${path}?${params.toString()}`, {
    headers: {
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
  });
  if (!response.ok) {
    let detail = `Export failed (${response.status})`;
    try {
      const payload = await response.json();
      detail = payload?.detail || detail;
    } catch {
      // Export endpoints normally return binary data.
    }
    throw new Error(detail);
  }
  return response.blob();
}

export function analyticsExportFilename(request: AnalyticsExportRequest) {
  const scope = request.targetName ? `${safeFilename(request.targetName)}_` : "";
  const stamp = formatDate(new Date()).replaceAll("-", "");
  return `${scope}calls_export_${stamp}.${request.format.toLowerCase()}`;
}

export async function downloadAnalyticsExport(request: AnalyticsExportRequest) {
  const params = new URLSearchParams({
    start_date: formatDate(request.start),
    end_date: formatDate(request.end),
  });
  if (request.agentId) params.set("agent_id", request.agentId);
  if (request.workflowId) params.set("workflow_id", request.workflowId);
  for (const [key, value] of Object.entries(request.filters || {})) {
    if (value) params.set(key, value);
  }

  const filename = analyticsExportFilename(request);
  if (request.format === "XLSX") {
    const csvBlob = await fetchExportBlob("/api/v1/analytics/export/csv", params);
    const XLSX = await import("xlsx");
    const workbook = XLSX.read(await csvBlob.arrayBuffer(), { type: "array" });
    XLSX.writeFile(workbook, filename);
    return filename;
  }

  const path = request.format === "PDF"
    ? "/api/v1/analytics/export/pdf"
    : "/api/v1/analytics/export/csv";
  saveBlob(await fetchExportBlob(path, params), filename);
  return filename;
}
